/**
 * @group Hooks
 * 
 * @summary
 * <blockquote>Hook for orchestrating the onboarding flow after user signup.</blockquote>
 * 
 * This hook handles:
 * - Signup via API or server action
 * - Automatic sign-in after successful signup
 * - Onboarding start API call
 * - Profile check and redirect logic
 * - Error handling and state management
 * 
 * @example
 * ```typescript
 * const {
 *   onboardingBlockedMessage,
 *   handleSignup,
 *   handleSignIn,
 * } = useOnboardingFlow({
 *   tenantUUID,
 *   useServerAction,
 *   toast,
 * });
 * ```
 */

import { useState } from 'react';
import { getSession, signIn } from 'next-auth/react';
import { FetchApiClient } from '@shared/httpService';
import { tryCatchAsync } from '@shared/utils';
import { Logger } from '@shared/logger';
import { UI_ACTIONS, AUTH_MESSAGES } from '@shared/constants';
import { OnboardingHttpService } from '../../../services/http/OnboardingHttpService';
import { handleTenantSignupError } from '../functions/handleTenantSignupError';
import { handleSignInError } from '../functions/handleSignInError';
import type { AuthFormValues } from './AuthFormValues';
import type { SignupResult } from './SignupResult';
import type { UseOnboardingFlowParams } from './UseOnboardingFlowParams';
import type { UseOnboardingFlowReturn } from './UseOnboardingFlowReturn';

/**
 * Hook for orchestrating onboarding flow
 */
export function useOnboardingFlow({
  tenantUUID,
  useServerAction = false,
  toast,
}: UseOnboardingFlowParams): UseOnboardingFlowReturn {
  const [onboardingBlockedMessage, setOnboardingBlockedMessage] = useState<string | null>(null);
  const fetchApi = new FetchApiClient();

  /**
   * Handles signup using server action
   * Note: Server action import is disabled due to monorepo configuration issues
   */
  async function handleSignupWithServerAction(values: AuthFormValues): Promise<SignupResult> {
    // Fallback to API call since server action import is not working
    return await handleSignupWithAPI(values);
  }

  /**
   * Handles signup using API call
   * Returns tenant name and onboarding info if signup was successful with tenantUUID, otherwise returns true/false
   */
  async function handleSignupWithAPI(values: AuthFormValues): Promise<SignupResult> {
    const response = await fetchApi.post('/api/auth/signup', {
      ...values,
      tenantUUID,
    });

    if (response.isErr) {
      const error = response.getFail();
      const errorCode = error.errorCode;
      
      // Handle invalid tenant UUID errors with clear message and link to individual signup
      if (handleTenantSignupError({ errorCode, toast })) {
        return { success: false };
      }
      
      toast({
        title: "Sign up failed",
        description: error.errorMessage || 'Something went wrong during sign up',
        variant: "destructive",
      });
      
      return { success: false };
    }

    const apiResponse = response.getSuccess();
    
    // Check if HTTP response indicates an error (even though processResponse returned ok)
    if (!apiResponse.ok) {
      // Extract error from response data
      const errorData = apiResponse.data as { errorCode?: string; message?: string; error?: string };
      const errorCode = errorData?.errorCode;
      
      // Handle invalid tenant UUID errors with clear message and link to individual signup
      if (handleTenantSignupError({ errorCode, toast })) {
        return { success: false };
      }
      
      // For other errors, show generic error but don't show authentication errors
      const errorMessage = errorData?.message || errorData?.error || 'Something went wrong during sign up';
      toast({
        title: "Sign up failed",
        description: errorMessage,
        variant: "destructive",
      });
      
      return { success: false };
    }

    const signupResponse = apiResponse.data as { 
      tenantName?: string;
      onboardingBlocked?: boolean;
      onboardingBlockReason?: string;
      pathwayType?: string;
      tenantLinked?: boolean;
    };
    
    return { 
      success: true, 
      tenantName: signupResponse.tenantName,
      onboardingBlocked: signupResponse.onboardingBlocked,
      onboardingBlockReason: signupResponse.onboardingBlockReason,
      pathwayType: signupResponse.pathwayType,
      tenantLinked: signupResponse.tenantLinked,
    };
  }

  /**
   * Handles automatic sign in after successful signup
   * Redirects to profile creation if no profile exists, otherwise to dashboard
   * Uses onboarding info from signup response if available
   */
  async function handleAutoSignInAfterSignup(
    values: AuthFormValues,
    signupResult?: {
      onboardingBlocked?: boolean;
      onboardingBlockReason?: string;
      pathwayType?: string;
      tenantLinked?: boolean;
    }
  ): Promise<void> {
    const result = await signIn('credentials', {
      redirect: false,
      email: values.email,
      password: values.password,
    });

    if (result?.error) {
      toast({
        title: "Sign in failed",
        description: result.error || 'Failed to sign in after registration',
        variant: "destructive",
      });
    } else {
      setOnboardingBlockedMessage(null);
      await handleReportAuthEvent(UI_ACTIONS.AUTH.SIGNUP, values.email);

      const session = await getSession();
      const sessionUserId = session?.user?.id;

      // Use onboarding info from signup response if available, otherwise call onboarding start API
      if (sessionUserId) {
        // If we already have onboarding info from signup response and it's blocked, don't call API
        if (signupResult?.onboardingBlocked && signupResult?.onboardingBlockReason) {
          // Already handled in handleSignup, just return
          return;
        }

        // Otherwise, call onboarding start API for consistency (ensures onboarding is started)
        const onboardingService = new OnboardingHttpService();
        const onboardingStartResult = await onboardingService.startOnboarding({
          tenantUUID,
          userId: sessionUserId,
        });

        if (onboardingStartResult.isErr) {
          const onboardingError = onboardingStartResult.getFail();
          const errorCode = onboardingError.errorCode;
          
          // Handle invalid tenant UUID errors with clear message and link to individual signup
          if (handleTenantSignupError({ errorCode, toast, setOnboardingBlockedMessage })) {
            return;
          }
          
          const onboardingErrorMessage = onboardingError.errorMessage || 'Failed to start onboarding';
          setOnboardingBlockedMessage(onboardingErrorMessage);
          toast({
            title: "Onboarding blocked",
            description: onboardingErrorMessage,
            variant: "destructive",
          });
          return;
        }

        const apiResponse = onboardingStartResult.getSuccess();
        
        // Check if HTTP response indicates an error
        if (!apiResponse.ok) {
          const errorData = apiResponse.data as { errorCode?: string; message?: string; error?: string };
          const errorCode = errorData?.errorCode;
          
          // Handle invalid tenant UUID errors with clear message and link to individual signup
          if (handleTenantSignupError({ errorCode, toast, setOnboardingBlockedMessage })) {
            return;
          }
          
          const errorMessage = errorData?.message || errorData?.error || 'Failed to start onboarding';
          setOnboardingBlockedMessage(errorMessage);
          toast({
            title: "Onboarding blocked",
            description: errorMessage,
            variant: "destructive",
          });
          return;
        }

        const onboardingStartResponse = apiResponse.data as {
          onboardingBlockReason?: string;
          onboardingBlocked?: boolean;
          pathwayType?: string;
        };

        if (onboardingStartResponse.onboardingBlocked) {
          // Map onboardingBlockReason to error code for consistent handling
          const errorCode = onboardingStartResponse.onboardingBlockReason === 'tenant_uuid_tampered'
            ? 'TENANT_UUID_TAMPERED'
            : 'INVALID_TENANT_UUID';
          
          if (handleTenantSignupError({ errorCode, toast, setOnboardingBlockedMessage })) {
            return;
          }
        }
      }
      
      // Check if user has profile and redirect accordingly
      const profileResult = await tryCatchAsync(async () => {
        const profileResponse = await fetchApi.get('/api/data/patient-profiles');
        
        if (profileResponse.isOk) {
          const apiResponse = profileResponse.getSuccess();
          if (apiResponse.ok) {
            const data = apiResponse.data as { profile?: unknown };
            // If profile exists, return true
            if (data && data.profile) {
              return true;
            }
          }
        }
        return false;
      });

      profileResult.match({
        ok: (hasProfile) => {
          window.location.href = hasProfile ? '/dashboard' : '/dashboard/patient-profile';
        },
        err: (error) => {
          // On error checking profile, default to dashboard (don't block user)
          Logger.error('Error checking profile after signup', { resource: 'auth.signup' }, new Error(error.errorMessage));
          window.location.href = '/dashboard';
        }
      });
    }
  }

  /**
   * Handles reporting authentication events
   */
  async function handleReportAuthEvent(type: 'signin' | 'signup', email: string): Promise<void> {
    return;
  }

  /**
   * Handles user sign in
   */
  async function handleSignIn(values: AuthFormValues): Promise<void> {
    const result = await signIn('credentials', {
      redirect: false,
      email: values.email,
      password: values.password,
    });

    if (result?.error) {
      handleSignInError(result.error, toast);
    } else {
      await handleReportAuthEvent(UI_ACTIONS.AUTH.SIGNIN, values.email);
      window.location.href = '/dashboard';
    }
  }

  /**
   * Handles the complete signup process
   */
  async function handleSignup(values: AuthFormValues): Promise<void> {
    const signupResult = useServerAction 
      ? await handleSignupWithServerAction(values)
      : await handleSignupWithAPI(values);

    if (signupResult.success) {
      // Handle onboarding info from signup response
      if (signupResult.onboardingBlocked && signupResult.onboardingBlockReason) {
        // Map onboardingBlockReason to error code for consistent handling
        const errorCode = signupResult.onboardingBlockReason === 'tenant_uuid_tampered'
          ? 'TENANT_UUID_TAMPERED'
          : 'INVALID_TENANT_UUID';
        
        handleTenantSignupError({ errorCode, toast, setOnboardingBlockedMessage });
      }
      
      // Show success notification with tenant name if signing up with tenantUUID
      if (signupResult.tenantName) {
        toast({
          title: "Successfully joined group",
          description: `${AUTH_MESSAGES.SUCCESS.TENANT_JOINED} ${signupResult.tenantName}`,
          variant: "default",
        });
      }
      
      await handleAutoSignInAfterSignup(values, signupResult);
    }
  }

  return {
    onboardingBlockedMessage,
    clearOnboardingBlockedMessage: () => setOnboardingBlockedMessage(null),
    handleSignup,
    handleSignIn,
  };
}
